// M8.4-fix-B (2026-04-23) — BookmarksPage
//
// Mục đích: NV xem lại tất cả câu hỏi đã 🔖 lưu trong quá trình hỏi AI.
// Lý do có trang này: trước đây nút "Bookmark" ở AskPage chỉ lưu nhưng không
// có trang để xem lại → NV không hiểu để làm gì (UAT feedback 2026-04-23).
//
// Flow:
//   1. Gate: L2 cho NV (admin bypass). Nếu chưa L2 → điều hướng về / (home sẽ mở picker).
//   2. GET /bookmarks (withL2) → list {id, question_id, question, answer, bo, topic, note, created_at}.
//   3. Render card list:
//      - Click card → expand answer inline (HTML đã sanitize server-side khi import,
//        nhưng FE vẫn dùng innerHTML có prefix stripHtml khi preview collapsed).
//      - Nút 🗑 xoá → DELETE /bookmarks/:questionId → optimistic remove.
//      - Badge bộ (bo) + topic.
//   4. Empty state: hướng dẫn NV hỏi AI và bấm 🔖 Lưu.
//
// Giới hạn: BE trả tối đa 200 rows gần nhất (ORDER BY created_at DESC).
// Nếu sau này NV có >200 bookmark → thêm pagination (future).

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';
// 2026-04-24 SEC: dùng safeRichHtml strict thay cho DOMPurify profile mềm + dedupe stripHtml local.
import { safeRichHtml, stripHtml } from '../lib/renderRichText.js';

const BO_LABELS = {
  chung:           'Chung',
  noi_quy:         'Nội quy',
  ke_toan:         'Kế toán',
  ban_hang:        'Bán hàng',
  cskh:            'CSKH',
  marketing:       'Marketing',
  admin_hr:        'HC-NS',
  cho_duyet:       'Chờ duyệt',
};

const BO_COLORS = {
  chung:           'bg-slate-100 text-slate-700 border-slate-200',
  noi_quy:         'bg-rose-50 text-rose-700 border-rose-200',
  ke_toan:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  ban_hang:        'bg-sky-50 text-sky-700 border-sky-200',
  cskh:            'bg-indigo-50 text-indigo-700 border-indigo-200',
  marketing:       'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  admin_hr:        'bg-amber-50 text-amber-700 border-amber-200',
  cho_duyet:       'bg-orange-50 text-orange-700 border-orange-200',
};

export default function BookmarksPage() {
  const { user, getValidL2SessionId, l2Employee } = useAuthStore();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === 'admin';
  const hasValidL2 = !!getValidL2SessionId() && !!l2Employee;

  useEffect(() => {
    // Admin không bookmark được (BE trả 400). Nếu admin vào trang này — redirect.
    if (isAdmin) {
      toast('Admin không có bookmark — cần phiên L2 của NV', { icon: 'ℹ️' });
      nav('/');
      return;
    }
    if (!hasValidL2) {
      toast.error('Vui lòng chọn NV và xác minh L2 trước');
      nav('/');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api.bookmarkList()
      .then((res) => {
        if (cancelled) return;
        setBookmarks(res?.bookmarks || []);
      })
      .catch((e) => {
        if (!cancelled) setErr(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isAdmin, hasValidL2, nav]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bookmarks;
    return bookmarks.filter((b) => {
      const hay = (b.question || '') + ' ' + (b.topic || '') + ' ' + (b.bo || '');
      return hay.toLowerCase().includes(q);
    });
  }, [bookmarks, search]);

  function toggleExpand(qid) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  }

  async function handleRemove(questionId) {
    const ok = await showConfirm({
      title: 'Xoá bookmark',
      message: 'Xoá khỏi danh sách đã lưu?',
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!ok) return;
    // Optimistic — rollback nếu fail
    const prev = bookmarks;
    setBookmarks((bs) => bs.filter((b) => b.question_id !== questionId));
    try {
      await api.bookmarkRemove(questionId);
      toast.success('Đã xoá khỏi danh sách');
    } catch (e) {
      setBookmarks(prev); // rollback
      const msg = e.data?.error || e.message || 'Xoá bookmark thất bại';
      toast.error(msg);
    }
  }

  return (
    <div className="min-h-full p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <header className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-2">
              <Link to="/" className="text-sm text-brand-600 hover:underline">← Trang chủ</Link>
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-800">
              🔖 Câu hỏi đã lưu
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Các câu trả lời bạn đã bấm <span className="font-medium">🔖 Lưu</span> khi hỏi AI — bấm card để xem đáp án đầy đủ.
            </p>
          </div>
          <Link
            to="/ask"
            className="px-3 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 text-sm font-medium border border-sky-200"
          >
            🔍 Hỏi đáp
          </Link>
        </header>

        {/* Search box */}
        {bookmarks.length > 0 && (
          <div className="mb-4">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm trong câu đã lưu…"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              data-testid="bookmarks-search"
            />
          </div>
        )}

        {/* Body */}
        {loading && (
          <div className="text-slate-400 text-sm py-8 text-center">Đang tải…</div>
        )}

        {!loading && err && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700">
            <div className="font-semibold mb-1">Không tải được danh sách</div>
            <div className="text-xs">{err.data?.error || err.message || 'Unknown error'}</div>
          </div>
        )}

        {!loading && !err && bookmarks.length === 0 && (
          <EmptyState />
        )}

        {!loading && !err && bookmarks.length > 0 && filtered.length === 0 && (
          <div className="text-slate-400 text-sm py-8 text-center">
            Không tìm thấy câu nào khớp "<span className="font-medium">{search}</span>".
          </div>
        )}

        {!loading && !err && filtered.length > 0 && (
          <ul className="space-y-3" data-testid="bookmarks-list">
            {filtered.map((b) => (
              <BookmarkCard
                key={b.id}
                bookmark={b}
                expanded={expanded.has(b.question_id)}
                onToggle={() => toggleExpand(b.question_id)}
                onRemove={() => handleRemove(b.question_id)}
              />
            ))}
          </ul>
        )}

        {!loading && !err && bookmarks.length >= 200 && (
          <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            ℹ️ Hiển thị 200 câu gần nhất. Xoá bớt câu cũ để lưu thêm.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BookmarkCard — 1 dòng trong danh sách
// ─────────────────────────────────────────────────────────────────────────────
function BookmarkCard({ bookmark, expanded, onToggle, onRemove }) {
  const bo = bookmark.bo || 'chung';
  const boLabel = BO_LABELS[bo] || bo;
  const boClass = BO_COLORS[bo] || BO_COLORS.chung;
  const preview = stripHtml(bookmark.answer);
  const previewShort = preview.length > 140 ? preview.slice(0, 140) + '…' : preview;
  const savedAt = formatDate(bookmark.created_at);

  return (
    <li
      className={`bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow transition ${
        expanded ? 'ring-2 ring-brand-200' : ''
      }`}
      data-testid={`bookmark-card-${bookmark.question_id}`}
    >
      {/* Header — bấm để expand */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-4 pt-3 pb-2"
      >
        <div className="flex items-start gap-2 flex-wrap">
          <span className={`shrink-0 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${boClass}`}>
            {boLabel}
          </span>
          {bookmark.topic && (
            <span className="shrink-0 inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
              {bookmark.topic}
            </span>
          )}
          {/* M128 (2026-04-27): badge phân biệt auto vs manual save */}
          {bookmark.auto_saved === 1 ? (
            <span
              className="shrink-0 inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
              title="Tự động lưu — sẽ bị xoá khi vượt 20 câu gần nhất. Bấm 'Lưu' để giữ vĩnh viễn."
            >
              🤖 Tự lưu
            </span>
          ) : (
            <span
              className="shrink-0 inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
              title="Bạn đã chủ động lưu câu này — giữ vĩnh viễn"
            >
              🔖 Đã lưu
            </span>
          )}
          <span className="shrink-0 ml-auto text-[11px] text-slate-400">{savedAt}</span>
        </div>
        <div className="mt-2 text-sm font-medium text-slate-800">
          {bookmark.question ? (
            <div className="rich-content"
                 dangerouslySetInnerHTML={safeRichHtml(bookmark.question)} />
          ) : (
            <em className="text-slate-400">(không có câu hỏi)</em>
          )}
        </div>
        {!expanded && previewShort && (
          <div className="mt-1 text-xs text-slate-500 line-clamp-2">{previewShort}</div>
        )}
        {bookmark.status === 'cho_duyet' && (
          <div className="mt-1 text-[11px] text-orange-600 italic">
            ⏳ Đang chờ admin duyệt
          </div>
        )}
      </button>

      {/* Expanded answer */}
      {expanded && (
        <div className="px-4 pb-3">
          <div className="mt-1 pt-2 border-t border-slate-100">
            {bookmark.answer ? (
              <div
                className="prose prose-sm max-w-none text-slate-700"
                dangerouslySetInnerHTML={safeRichHtml(bookmark.answer)}
              />
            ) : (
              <div className="text-slate-400 text-sm italic">(Câu này chưa có đáp án)</div>
            )}
            {bookmark.note && (
              <div className="mt-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                <span className="font-medium">Ghi chú:</span> {bookmark.note}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="px-4 pb-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded"
          data-testid={`bookmark-toggle-${bookmark.question_id}`}
        >
          {expanded ? '▲ Thu gọn' : '▼ Xem đáp án'}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-rose-600 hover:text-white hover:bg-rose-500 border border-rose-200 hover:border-rose-500 bg-rose-50 px-2 py-1 rounded transition"
          title="Xoá khỏi danh sách đã lưu"
          data-testid={`bookmark-remove-${bookmark.question_id}`}
        >
          🗑 Xoá
        </button>
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
      <div className="text-5xl mb-3">🔖</div>
      <h2 className="font-semibold text-slate-700">Chưa có câu nào được lưu</h2>
      <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
        Khi bạn <Link to="/ask" className="text-brand-600 hover:underline">hỏi AI</Link> và thấy câu trả lời hữu ích,
        bấm nút <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-[11px] font-medium text-amber-700 mx-1">🔖 Lưu</span>
        để tra lại nhanh ở trang này mà không cần hỏi lại AI (tiết kiệm chi phí).
      </p>
      <Link
        to="/ask"
        className="mt-4 inline-block px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium"
      >
        🔍 Đi hỏi AI ngay
      </Link>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now - d;
    const diffHr = diffMs / 3_600_000;
    if (diffHr < 1) {
      const diffMin = Math.max(1, Math.round(diffMs / 60_000));
      return `${diffMin} phút trước`;
    }
    if (diffHr < 24) return `${Math.round(diffHr)} giờ trước`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7) return `${diffDay} ngày trước`;
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '';
  }
}
